import { constructQueryParamName } from './search';
import { stringify } from './urlHelpers';

/**
 * Browse URLs use path `/browse/:slug` (see BrowseCategoryRedirectPage).
 * Redirect applies `pub_categoryLevel1` using ids below — align with listing-categories in Console.
 */
export const BROWSE_CATEGORY_SLUG_TO_LEVEL1_ID = {
  camping: 'camping',
  cycling: 'cycling',
  watersports: 'watersports',
  hiking: 'hiking',
  snowsports: 'snowsports',
  climbing: 'climbing',
};

export const BROWSE_CATEGORY_ORDER = Object.keys(BROWSE_CATEGORY_SLUG_TO_LEVEL1_ID);

const normalizeSlug = slug => (typeof slug === 'string' ? slug.trim().toLowerCase() : '');

/** @returns {string|null} */
export const browseCategorySlugToPublicCategoryLevel1 = slug => {
  const key = normalizeSlug(slug);
  return BROWSE_CATEGORY_SLUG_TO_LEVEL1_ID[key] ?? null;
};

export const isValidBrowseCategorySlug = slug =>
  browseCategorySlugToPublicCategoryLevel1(slug) != null;

/**
 * Link configs for Topbar custom links (desktop LinksMenu + mobile menu).
 * Inserted after primary hosted links, before other secondary links.
 *
 * Each link is rendered via NamedLink and points directly to SearchPage with
 * the `pub_categoryLevel1` filter applied, so users land on `/s?pub_categoryLevel1=<id>`
 * without going through the `/browse/<slug>` redirect step.
 *
 * Labels and ids are sourced from hosted listing-categories configuration
 * (`config.categoryConfiguration`) so they stay in sync with Console updates
 * and require no slug→id mapping in the template.
 *
 * @param {Object} config app configuration containing `categoryConfiguration`
 * @returns {Array<{ group: string, text: string, type: string, route: object }>}
 */
export const buildBrowseCategoryTopbarLinkConfigs = config => {
  const listingCategories = config?.categoryConfiguration?.categories;
  const categoryKeyPrefix = config?.categoryConfiguration?.key;
  if (!Array.isArray(listingCategories) || listingCategories.length === 0 || !categoryKeyPrefix) {
    return [];
  }

  const categoryQueryParam = constructQueryParamName(`${categoryKeyPrefix}1`, 'public');

  return listingCategories.map(category => ({
    group: 'secondary',
    text: category.name,
    type: 'internal',
    route: {
      name: 'SearchPage',
      params: {},
      to: { search: `?${stringify({ [categoryQueryParam]: category.id })}` },
    },
  }));
};

const MAIN_SEARCH_ROUTE_NAME = 'SearchPage';
const MENU_LABEL_TRANSLATION_KEY = 'TopbarDesktop.LinksMenu.all';
const MORE_LABEL_TRANSLATION_KEY = 'TopbarDesktop.LinksMenu.more';

/**
 * Detects hosted custom links that duplicate the dropdown parent label ("Menu"/"More").
 * Those should not appear as submenu items.
 *
 * @param {{ text?: string }} link
 * @param {import('react-intl').IntlShape} intl
 * @returns {boolean}
 */
const isMenuLabelLink = (link, intl) => {
  const text = typeof link?.text === 'string' ? link.text.trim().toLowerCase() : '';
  const menuLabel = intl.formatMessage({ id: MENU_LABEL_TRANSLATION_KEY }).trim().toLowerCase();
  const moreLabel = intl.formatMessage({ id: MORE_LABEL_TRANSLATION_KEY }).trim().toLowerCase();
  return text.length > 0 && (text === menuLabel || text === moreLabel);
};

const isMainSearchPageTopbarLink = link => {
  if (link.route?.name === MAIN_SEARCH_ROUTE_NAME) {
    return true;
  }
  const href = link.href;
  if (typeof href !== 'string' || href.length === 0) {
    return false;
  }
  const isInternal = link.type === 'internal' || href.charAt(0) === '/';
  if (!isInternal) {
    return false;
  }
  try {
    const testURL = new URL('http://my.marketplace.com' + (href.startsWith('/') ? href : `/${href}`));
    return testURL.pathname === '/s';
  } catch (e) {
    return false;
  }
};

/**
 * Builds the ordered topbar link list:
 * - hosted primary links
 * - browse category links in Menu (sourced from hosted listing-categories)
 * - remaining hosted secondary links (excluding duplicate Menu label links)
 *
 * @param {Array<object>} resolvedCustomLinks output of Topbar getResolvedCustomLinks
 * @param {import('react-intl').IntlShape} intl
 * @param {Object} config app configuration containing `categoryConfiguration`
 * @returns {Array<object>}
 */
export const mergeTopbarCustomLinksWithBrowseCategories = (resolvedCustomLinks, intl, config) => {
  const list = Array.isArray(resolvedCustomLinks)
    ? resolvedCustomLinks.filter(l => !isMainSearchPageTopbarLink(l))
    : [];
  const primaryCustomLinks = list.filter(l => l.group === 'primary');

  const secondaryCustomLinks = list.filter(l => l.group !== 'primary');
  const otherSecondaries = secondaryCustomLinks.filter(l => !isMenuLabelLink(l, intl));

  return [
    ...primaryCustomLinks,
    ...buildBrowseCategoryTopbarLinkConfigs(config),
    ...otherSecondaries,
  ];
};
